<template>
  <MonthlyConfigurationShell
    active-tab="attendance"
    :skip-privilege-check="skipPrivilegeCheck"
    :monthly-privilege-name="monthlyPrivilegeName"
    :earn-privilege-name="earnPrivilegeName"
    :attendance-privilege-name="attendancePrivilegeName"
    @navigate="$emit('navigate', $event)"
  >
    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>
    <div class="px-6 pb-6 pt-2">
      <div class="w-full max-lg:overflow-x-auto">
        <form class="pb-5 pt-2 mt-5" @submit.prevent>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">{{ selectMonthLabel }}</label>
              <select
                v-model="filters.selectedMonth"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
              >
                <option value="" disabled>Select Month</option>
                <option
                  v-for="month in monthOptions"
                  :key="month.value"
                  :value="month.value"
                >
                  {{ month.label }}
                </option>
              </select>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">From Date</label>
              <input
                v-model="filters.fromDate"
                type="date"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">{{ toDateLabel }}</label>
              <input
                v-model="filters.toDate"
                type="date"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Branch</label>
              <select
                v-model="filters.selectedBranchId"
                :disabled="isLoadingBranches || hasFixedBranchSelection || !isPeriodReady"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
                required
              >
                <option value="" disabled>
                  {{ isLoadingBranches ? 'Loading branches...' : attendanceBranchOptions.length === 0 ? 'No branches available' : 'Select Branch' }}
                </option>
                <option
                  v-for="option in attendanceBranchOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

          <!-- Select Department -->
<div class="flex flex-col gap-1">
  <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">
    Select Department
  </label>

  <select
    v-model="filters.departmentId"
    :disabled="!filters.selectedBranchId || isLoadingDepartments"
    class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
  >
    <option value="" disabled>
      {{
        !filters.selectedBranchId
          ? 'Select Branch first'
          : isLoadingDepartments
            ? 'Loading departments...'
            : departmentOptions.length === 0
              ? 'No active departments'
              : 'Select Department'
      }}
    </option>
    <option
      v-for="dep in departmentOptions"
      :key="dep.id"
      :value="String(dep.id)"
    >
      {{ dep.name }}
    </option>
  </select>
</div>

<!-- Multi Department -->
<div class="flex flex-col gap-1">
  <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">
    Multi Department Users
  </label>

  <button
    v-if="canShowMultiDepartmentChip"
    type="button"
    class="multi-dept-trigger h-[30px] w-full"
    :disabled="isLoadingMultiDepartmentUsers || multiDepartmentUsersCount === 0"
    :title="multiDepartmentUsersCount === 0 ? 'No multiple-department users' : 'View multiple-department users'"
    @click="onMultiDepartmentDetail"
  >
    {{
      isLoadingMultiDepartmentUsers
        ? 'Loading...'
        : `Multi Dept: ${multiDepartmentUsersCount}`
    }}
  </button>

  <div
    v-else
    class="h-[34px] border border-[#cbd5e1] rounded-md bg-slate-100"
  ></div>
</div>
          </div>
        </form>

        <div v-if="isLoadingDepartments" class="py-8 flex justify-center items-center">
          <div class="attendance-spinner h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full"></div>
        </div>

        <table v-else class="attendance-table w-full max-lg:min-w-[640px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
          <thead>
            <tr>
              <th>No.</th>
              <th>Department</th>
              <th>Employee</th>
              <th>Status</th>
              <th>Action</th>
              <th>Approval</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in attendanceRows"
              :key="row.id"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td>{{ idx + 1 }}</td>
              <td>{{ row.departmentName }}</td>
              <td>{{ row.employeeCount }}</td>
              <td :class="statusClassName(row.status)">{{ row.employeeCount>0?row.status:'' }}</td>
              <td class="actions">
                <button type="button" @click="onDetail(row)">Detail</button>
              </td>
              <td>{{ row.approvalStatus }}</td>
            </tr>
            <tr v-if="attendanceRows.length === 0">
              <td colspan="6" class="empty-cell">
                {{ emptyStateMessage }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="overtimePanel.open"
        ref="overtimePanelEl"
        class="fixed z-[10001] flex flex-col bg-white border border-[#cbd5e1] rounded-md shadow-xl text-left w-[22rem] max-w-[calc(100vw-1rem)]"
        :style="{ top: overtimePanel.top + 'px', left: overtimePanel.left + 'px', maxHeight: overtimePanel.maxH + 'px' }"
        @click.stop
      >
        <div class="shrink-0 px-3 py-2 text-[11px] font-bold text-[#0d4169] border-b border-slate-100">
          Select OT and hours
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto pb-10">
          <div
            v-for="ot in overtimeOptions"
            :key="ot.id"
            class="flex items-center gap-2 px-3 py-1.5 border-b border-slate-50 hover:bg-slate-50"
          >
            <label class="text-xs font-medium text-slate-700 flex-1 truncate">{{ ot.otName }}</label>
            <input
              type="number"
              step="0.25"
              min="0"
              :value="overtimeHoursForCurrent(ot.id)"
              class="ot-hours-input"
              placeholder="Hrs"
              @input="setOvertimeHoursForCurrent(ot.id, $event.target.value)"
            >
          </div>
          <p v-if="overtimeOptions.length === 0" class="text-xs text-slate-400 px-3 py-4 text-center">No active overtime definitions</p>
        </div>
        <button
          type="button"
          class="absolute bottom-2 right-2 text-[11px] font-bold text-[#2563eb] underline"
          @click="closeOvertimePanel"
        >
          Close
        </button>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="attendanceDetailModal.show"
        class="fixed inset-0 z-[10000] flex items-start justify-center px-4 pt-6 pb-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
        @click.self="closeAttendanceDetailModal"
      >
        <div
          class="bg-white rounded-xl shadow-2xl w-full max-w-4xl transform transition-all relative overflow-hidden"
        >
          <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
            <h2 class="text-lg font-bold m-0">Attendance Detail</h2>
            <button
              type="button"
              class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
              @click="closeAttendanceDetailModal"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="p-6">
            <p v-if="attendanceDetailModal.departmentName" class="text-sm text-slate-600 mb-4 m-0">
              {{ attendanceDetailModal.isMultiDepartment ? 'Group' : 'Department' }}:
              <span class="font-semibold text-slate-800">{{ attendanceDetailModal.departmentName }}</span>
              <span v-if="totalDays > 0" class="ml-3 text-slate-500">(T. Days: {{ totalDays }})</span>
            </p>

            <div class="overflow-x-auto rounded-lg border border-slate-200">
              <table class="attendance-detail-table w-full min-w-[720px] border-collapse">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Emp. Id</th>
                    <th>Emp. Name</th>
                    <th>Role</th>
                    <th>Due Date</th>
                    <th class="num">Overtime</th>
                    <th class="num">Total Hrs</th>
                    <th class="num">Absent</th>
                    <th class="num">Leave Days</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(emp, idx) in attendanceDetailModal.employees"
                    :key="emp.empId"
                    class="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td>{{ idx + 1 }}</td>
                    <td>{{ emp.empId }}</td>
                    <td>{{ emp.empName }}</td>
                    <td>{{ emp.role }}</td>
                    <td>{{ resolveAttendanceDueDate(emp.totalDays) }}</td>
                    <td class="num">
                      <button
                        type="button"
                        class="ot-action-btn"
                        @click.stop="openOvertimePanel($event, emp)"
                      >
                        {{ overtimeButtonLabel(emp) }} ▾
                      </button>
                    </td>
                    <td class="num">{{ fmtOvertimeTotalHours(emp) }}</td>
                    <td class="num">
                      <input
                        v-model.number="emp.absent"
                        type="number"
                        step="1"
                        min="0"
                        class="income-table-field"
                        :class="{ 'opacity-60 cursor-not-allowed bg-slate-100': lockAbsentLeaveDays }"
                        :disabled="lockAbsentLeaveDays"
                        :readonly="lockAbsentLeaveDays"
                      >
                    </td>
                    <td class="num">
                      <input
                        v-model.number="emp.leaveDays"
                        type="number"
                        step="1"
                        min="0"
                        class="income-table-field"
                        :class="{ 'opacity-60 cursor-not-allowed bg-slate-100': lockAbsentLeaveDays }"
                        :disabled="lockAbsentLeaveDays"
                        :readonly="lockAbsentLeaveDays"
                      >
                    </td>
                    <td class="actions">
                      <button type="button" @click="onEmployeeDetail(emp)">Detail</button>
                      <button type="button" @click="onEmployeeEdit(emp)">Edit</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p
              class="text-center text-sm mt-4 mb-0"
            ></p>

            <div v-if="filters.selectedMonth" class="flex justify-center pt-6">
              <button
                type="button"
                :disabled="!canPrepareAttendance || isSaving"
                class="min-w-[120px] px-8 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                @click="onSave"
              >
                {{ isSaving ? 'Saving...' : 'Save' }}
              </button>
            </div>

            <div class="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-2 pt-4">
              <span
                :title="preparedByTooltip || undefined"
                class="attendance-prepared-by-label px-2 py-2 text-xs font-semibold text-slate-700 bg-slate-200/80 rounded-md text-center cursor-default select-none"
              >
                {{ preparedByButtonLabel }}
              </span>
              <button
                v-for="action in otherApprovalActions"
                :key="action.key"
                type="button"
                :disabled="!action.canClick || isUpdatingApproval"
                :title="action.tooltip || undefined"
                class="px-2 py-2 text-xs font-semibold text-slate-700 bg-slate-200/80 rounded-md transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed"
                :class="action.canClick && !isUpdatingApproval ? 'hover:bg-slate-300' : 'cursor-default'"
                @click="onApprovalAction(action.key)"
              >
                {{ action.label }}
              </button>
            </div>
          </div>

          <div class="px-6 py-4 flex justify-end">
            <button
              type="button"
              class="px-6 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800 transition-colors"
              @click="closeAttendanceDetailModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </MonthlyConfigurationShell>
</template>

<script setup>
import { computed, defineEmits, defineProps, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import MonthlyConfigurationShell from './monthlyConfigurationShell.vue'
import FinanceToast from '../FinanceToast.vue'
import { apiFetch } from '../../../utils/api'
import {
  ETHIOPIAN_MONTH_DAYS,
  ETHIOPIAN_MONTH_OPTIONS,
  formatMonthOptionWithAmharic
} from '../../../utils/ethiopianMonth'
import { useMonthlyConfigurationBranches } from './useMonthlyConfigurationBranches.js'
import { isMainCompanyScope } from './monthlyConfigurationScope.js'

const props = defineProps({
  apiBase: {
    type: String,
    default: '/finance'
  },
  skipPrivilegeCheck: {
    type: Boolean,
    default: false
  },
  privilegeName: {
    type: String,
    default: 'Attendance'
  },
  monthlyPrivilegeName: {
    type: String,
    default: 'Monthly Configuration'
  },
  earnPrivilegeName: {
    type: String,
    default: 'Earn And Deduct'
  },
  attendancePrivilegeName: {
    type: String,
    default: 'Attendance'
  }
})

const monthlyConfigurationApiPath = computed(() => `${props.apiBase}/monthly-configuration`)

/** HR Attendance Configuration only — finance keeps prior behavior. */
const isHrRoute = computed(() => {
  const base = String(props.apiBase || '').trim().replace(/\/+$/, '')
  return base === '/hr'
})

/** Loaded with department list when routed from HR. */
const isAttendanceDevice = ref(0)
const lockAbsentLeaveDays = computed(
  () => isHrRoute.value && Number(isAttendanceDevice.value) === 1
)

const store = useStore()
const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canPrepareAttendance = computed(() => {
  if (props.skipPrivilegeCheck) return true
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.privilegeName), 'Prepared By')
})

function canPerformApprovalAction(action) {
  if (props.skipPrivilegeCheck) return true
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.privilegeName), action)
}

function firstNameFromFullName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean)
  return parts[0] || ''
}

function uniqueApproversByField(idField, nameField) {
  const seen = new Set()
  const list = []
  for (const row of savedAttendanceRecords.value) {
    const id = row?.[idField]
    if (!id || seen.has(id)) continue
    seen.add(id)
    list.push({
      id,
      fullName: String(row?.[nameField] || '').trim() || `User #${id}`
    })
  }
  return list
}

function buildApprovalButtonLabel(baseLabel, approvers) {
  if (!approvers.length) return baseLabel
  if (approvers.length === 1) {
    const name = firstNameFromFullName(approvers[0].fullName)
    return name ? `${baseLabel} ${name}` : baseLabel
  }
  const first = firstNameFromFullName(approvers[0].fullName)
  return first ? `${baseLabel} ${first} and others` : `${baseLabel} and others`
}

function buildApprovalTooltip(approvers) {
  if (approvers.length <= 1) return ''
  return approvers.map((p) => p.fullName).join(', ')
}

const savedAttendanceRecords = ref([])
const isUpdatingApproval = ref(false)

const uniquePreparers = computed(() => uniqueApproversByField('preparedBy', 'preparedByFullName'))

const preparedByButtonLabel = computed(() => buildApprovalButtonLabel('Prepared By', uniquePreparers.value))

const preparedByTooltip = computed(() => buildApprovalTooltip(uniquePreparers.value))

const otherApprovalActions = computed(() => {
  const checkedApprovers = uniqueApproversByField('checkedBy', 'checkedByFullName')
  const crossCheckedApprovers = uniqueApproversByField('crossCheckedBy', 'crossCheckedByFullName')
  const approvedApprovers = uniqueApproversByField('approvedBy', 'approvedByFullName')

  return [
    {
      key: 'checked',
      baseLabel: 'Checked By',
      label: buildApprovalButtonLabel('Checked By', checkedApprovers),
      tooltip: buildApprovalTooltip(checkedApprovers),
      canClick: canPerformApprovalAction('Checked By') && uniquePreparers.value.length > 0 && savedAttendanceRecords.value.length > 0
    },
    {
      key: 'cross_checked',
      baseLabel: 'Cross Checked By',
      label: buildApprovalButtonLabel('Cross Checked By', crossCheckedApprovers),
      tooltip: buildApprovalTooltip(crossCheckedApprovers),
      canClick: canPerformApprovalAction('Cross Checked By') && checkedApprovers.length > 0 && savedAttendanceRecords.value.length > 0
    },
    {
      key: 'approved',
      baseLabel: 'Approved By',
      label: buildApprovalButtonLabel('Approved By', approvedApprovers),
      tooltip: buildApprovalTooltip(approvedApprovers),
      canClick: canPerformApprovalAction('Approved By') && crossCheckedApprovers.length > 0 && savedAttendanceRecords.value.length > 0
    }
  ]
})

defineEmits(['navigate'])

const { branchSelectOptions, isLoadingBranches, loadBranches, ALL_BRANCHES_SCOPE_VALUE } =
  useMonthlyConfigurationBranches()

const MULTI_DEPARTMENT_MODAL_ID = 'multi_department'

const tokenBranchId = computed(() => {
  const raw =
    currentUser.value?.branchId ??
    currentUser.value?.branch_id ??
    currentUser.value?.branch?.id ??
    null
  if (raw === null || raw === undefined || String(raw).trim() === '') return ''
  return String(raw).trim()
})
const tokenBranchName = computed(() => {
  const fromBranch = currentUser.value?.branch?.name
  if (fromBranch) return String(fromBranch)
  return ''
})
const hasFixedBranchSelection = computed(() => String(tokenBranchId.value || '').trim() !== '')

const isPeriodReady = computed(() => {
  return Boolean(filters.selectedMonth) || Boolean(filters.fromDate && filters.toDate)
})

const isSaving = ref(false)
const toast = ref({ show: false, message: '', variant: 'success' })
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.value = { show: true, message, variant }
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.value = { show: false, message: '', variant: 'success' }
    toastTimer = null
  }, duration)
}

const filters = reactive({
  selectedMonth: '',
  fromDate: '',
  toDate: '',
  selectedBranchId: '',
  departmentId: ''
})

const monthOptions = ETHIOPIAN_MONTH_OPTIONS.map((month) => ({
  value: month.value,
  label: formatMonthOptionWithAmharic(month)
}))

const selectMonthLabel = computed(() => {
  // Ethiopian business months are always 30 days (same as payroll).
  if (!filters.selectedMonth) return 'Select Month'
  return `Select Month (${ETHIOPIAN_MONTH_DAYS} Days)`
})

const toDateLabel = computed(() => {
  if (!filters.fromDate || !filters.toDate) return 'To Date'
  const from = new Date(`${filters.fromDate}T00:00:00`)
  const to = new Date(`${filters.toDate}T00:00:00`)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return 'To Date'
  const diffMs = to.getTime() - from.getTime()
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1
  return `To Date (${days} Days)`
})

watch(
  () => filters.selectedMonth,
  (month) => {
    if (month) {
      filters.fromDate = ''
      filters.toDate = ''
    }
  }
)

watch(
  () => [filters.fromDate, filters.toDate],
  ([fromDate, toDate]) => {
    if (fromDate || toDate) {
      filters.selectedMonth = ''
    }
  }
)

const departments = ref([])
const departmentUsersMap = ref({})
const multiDepartmentUsers = ref([])
const isLoadingDepartments = ref(false)
const isLoadingMultiDepartmentUsers = ref(false)
const overtimeOptions = ref([])
const overtimePanelEl = ref(null)
const overtimePanel = reactive({
  open: false,
  top: 0,
  left: 0,
  maxH: 340,
  empId: ''
})

function departmentIsActive(row) {
  if (!row || typeof row !== 'object') return false
  const status = row.status
  // New attendance endpoint does not return status; treat as active.
  if (status === undefined || status === null) return true
  return status === 1 || status === '1' || status === true
}

function splitDepartmentTokens(value) {
  if (value == null || value === '') return []
  return String(value)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function hasSingleDepartment(departmentsValue) {
  return splitDepartmentTokens(departmentsValue).length === 1
}

function isSingleDepartmentUserForRow(user, departmentName) {
  const target = String(departmentName || '').trim()
  if (!target || !user) return false

  const departmentsValue = user.departments
  if (departmentsValue != null && String(departmentsValue).trim() !== '') {
    if (!hasSingleDepartment(departmentsValue)) return false
    return splitDepartmentTokens(departmentsValue)[0] === target
  }

  return false
}

function getSingleDepartmentUsers(departmentId, departmentName) {
  const users = departmentUsersMap.value[String(departmentId)] || []
  return users.filter((user) => isSingleDepartmentUserForRow(user, departmentName))
}

async function loadDepartments(selectedBranchId) {
  const scopeId = selectedBranchId != null && String(selectedBranchId).trim() !== ''
    ? String(selectedBranchId).trim()
    : null

  isLoadingDepartments.value = true
  try {
    let scope = scopeId
    if (!scope) {
      scope = 'main_company'
    }
    if (scope === String(ALL_BRANCHES_SCOPE_VALUE)) {
      scope = 'all'
    } else if (isMainCompanyScope(scope)) {
      scope = 'main_company'
    }

    const periodParams = new URLSearchParams()
    if (filters.selectedMonth) {
      periodParams.set('month', String(filters.selectedMonth))
    } else if (filters.fromDate && filters.toDate) {
      periodParams.set('fromDate', filters.fromDate)
      periodParams.set('toDate', filters.toDate)
    }
    const periodQuery = periodParams.toString()
    const withPeriod = (url) => (periodQuery ? `${url}&${periodQuery}` : url)

    const requestUrls = []
    if (scope === String(ALL_BRANCHES_SCOPE_VALUE) || scope === 'all') {
      requestUrls.push(withPeriod(`${monthlyConfigurationApiPath.value}/attendance-departments-by-scope?selection=all`))
    } else if (scope === 'main_company') {
      requestUrls.push(withPeriod(`${monthlyConfigurationApiPath.value}/attendance-departments-by-scope?selection=main_company`))
    } else {
      requestUrls.push(
        withPeriod(`${monthlyConfigurationApiPath.value}/attendance-departments-by-scope?selection=branch&branchId=${encodeURIComponent(scope)}`)
      )
    }

    // Keep existing endpoint as fallback for compatibility.
    requestUrls.push(withPeriod(`${monthlyConfigurationApiPath.value}/attendance-departments?scope=${encodeURIComponent(scope)}`))

    let response = null
    for (const url of requestUrls) {
      const candidate = await apiFetch(url, { method: 'GET' })
      if (candidate.ok) {
        response = candidate
        break
      }
    }

    if (!response) {
      departments.value = []
      if (isHrRoute.value) isAttendanceDevice.value = 0
      return
    }

    const data = await response.json().catch(() => [])
    let rows = []
    if (Array.isArray(data)) {
      rows = data
      if (isHrRoute.value) isAttendanceDevice.value = 0
    } else if (data && typeof data === 'object') {
      rows = Array.isArray(data.departments) ? data.departments : []
      if (isHrRoute.value) {
        isAttendanceDevice.value = Number(data.is_attendance_device) === 1 ? 1 : 0
      }
    } else if (isHrRoute.value) {
      isAttendanceDevice.value = 0
    }
    const filteredRows = rows.filter(departmentIsActive)
    departments.value = filteredRows
    departmentUsersMap.value = filteredRows.reduce((acc, dep) => {
      const key = String(dep?.id ?? '')
      if (!key) return acc
      acc[key] = Array.isArray(dep?.users) ? dep.users : []
      return acc
    }, {})
  } catch (err) {
    console.error('Error loading departments for attendance monthly configuration:', err)
    departments.value = []
    departmentUsersMap.value = {}
    if (isHrRoute.value) isAttendanceDevice.value = 0
  } finally {
    isLoadingDepartments.value = false
  }
}

const departmentOptions = computed(() => departments.value)

const attendanceBranchOptions = computed(() => {
  const forcedBranchId = tokenBranchId.value
  if (!forcedBranchId) {
    return branchSelectOptions.value
  }

  const matched = branchSelectOptions.value.find(
    (option) => String(option.value || '').trim() === forcedBranchId
  )
  if (matched) {
    return [{ value: forcedBranchId, label: matched.label || tokenBranchName.value || 'Branch' }]
  }

  return [{ value: forcedBranchId, label: tokenBranchName.value || 'Branch' }]
})

watch(
  tokenBranchId,
  (forcedBranchId) => {
    const fixed = String(forcedBranchId || '').trim()
    if (fixed && String(filters.selectedBranchId || '').trim() !== fixed) {
      filters.selectedBranchId = fixed
    }
  },
  { immediate: true }
)

const attendanceDetailModal = reactive({
  show: false,
  departmentId: null,
  departmentName: '',
  isMultiDepartment: false,
  employees: []
})

const multiDepartmentUsersCount = computed(() => multiDepartmentUsers.value.length)

const canShowMultiDepartmentChip = computed(() => {
  return (
    String(filters.selectedBranchId || '').trim() !== '' &&
    isPeriodReady.value &&
    !isLoadingDepartments.value
  )
})

function buildMultiDepartmentScopeQuery() {
  const params = new URLSearchParams()
  const branchId = String(filters.selectedBranchId || '').trim()

  if (branchId === String(ALL_BRANCHES_SCOPE_VALUE)) {
    params.set('selection', 'all')
  } else if (isMainCompanyScope(branchId)) {
    params.set('selection', 'main_company')
  } else {
    params.set('selection', 'branch')
    params.set('branchId', branchId)
  }

  if (filters.selectedMonth) {
    params.set('month', String(filters.selectedMonth))
  } else if (filters.fromDate && filters.toDate) {
    params.set('fromDate', filters.fromDate)
    params.set('toDate', filters.toDate)
  }

  return params
}

async function loadMultiDepartmentUsers() {
  const branchSelected = String(filters.selectedBranchId || '').trim() !== ''
  if (!branchSelected || !hasValidAttendancePeriod()) {
    multiDepartmentUsers.value = []
    return
  }

  isLoadingMultiDepartmentUsers.value = true
  try {
    const params = buildMultiDepartmentScopeQuery()
    const response = await apiFetch(
      `${monthlyConfigurationApiPath.value}/attendance-multi-department-users?${params.toString()}`,
      { method: 'GET' }
    )
    if (!response.ok) {
      multiDepartmentUsers.value = []
      return
    }
    const data = await response.json().catch(() => ({}))
    const users = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : []
    multiDepartmentUsers.value = users
  } catch (err) {
    console.error('Error loading multi-department users for attendance:', err)
    multiDepartmentUsers.value = []
  } finally {
    isLoadingMultiDepartmentUsers.value = false
  }
}

function attendanceDraftKeyForDepartment(departmentId) {
  const user = currentUser.value || {}
  const companyId = user?.companyId ?? user?.company_id ?? user?.company?.id ?? 'na'
  const branchId = String(filters.selectedBranchId || tokenBranchId.value || 'na')
  const depId = String(departmentId || 'na')
  const periodKey = periodStorageKeyPart()
  return `${ATTENDANCE_DRAFT_PREFIX}:${companyId}:${branchId}:${depId}:${periodKey}`
}

function hasAttendanceDraftForDepartment(departmentId) {
  if (!departmentId) return false
  if (!hasValidAttendancePeriod()) return false
  const key = attendanceDraftKeyForDepartment(departmentId)
  let raw = null
  try {
    raw = localStorage.getItem(key)
  } catch {
    raw = null
  }
  if (!raw) return false
  const parsed = safeJsonParse(raw)
  if (!parsed || typeof parsed !== 'object') return false
  if (String(parsed.departmentId || '') !== String(departmentId)) return false
  const monthMatch = String(parsed.month || '') === String(filters.selectedMonth || '')
  const rangeMatch =
    String(parsed.fromDate || '') === String(filters.fromDate || '') &&
    String(parsed.toDate || '') === String(filters.toDate || '')
  return monthMatch || rangeMatch
}

function resolveDepartmentStatus(departmentId) {
  const dep = departments.value.find((row) => String(row?.id || '').trim() === String(departmentId || '').trim())
  if (!dep) return 'New'
  if (hasAttendanceDraftForDepartment(dep.id)) return 'Drafted'
  if (dep.hasAttendance === true) return 'Approved'
  return 'New'
}

function statusClassName(status) {
  if (status === 'Drafted') return 'status-drafted'
  if (status === 'Approved') return 'status-approved'
  return 'status-new'
}

const attendanceRows = computed(() => {
  const _draftRevision = attendanceDraftRevision.value
  void _draftRevision
  const branchSelected = String(filters.selectedBranchId || '').trim() !== ''
  if (!branchSelected) return []

  const selectedDepId = String(filters.departmentId || '').trim()
  const rows = selectedDepId
    ? departments.value.filter((d) => String(d.id) === selectedDepId)
    : departments.value

  const sortedRows = [...rows].sort((a, b) => {
    const aName = String(a?.name || '').trim()
    const bName = String(b?.name || '').trim()
    return aName.localeCompare(bName, undefined, { sensitivity: 'base' })
  })

  return sortedRows.map((dep) => ({
    id: dep.id,
    departmentName: dep.name || '—',
    employeeCount: getSingleDepartmentUsers(dep.id, dep.name).length,
    status: resolveDepartmentStatus(dep.id),
    approvalStatus: String(dep.approvalStatus || '').trim()
  }))
})

function resolveTotalDays() {
  if (filters.fromDate && filters.toDate) {
    const from = new Date(`${filters.fromDate}T00:00:00`)
    const to = new Date(`${filters.toDate}T00:00:00`)
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return 0
    const diffMs = to.getTime() - from.getTime()
    return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1
  }
  if (filters.selectedMonth) {
    return ETHIOPIAN_MONTH_DAYS
  }
  return ETHIOPIAN_MONTH_DAYS
}

/** Same default as payroll: Due Date is 30 when nothing valid is saved. */
function resolveAttendanceDueDate(rawDueDate) {
  const due = Number(rawDueDate)
  if (Number.isFinite(due) && due > 0) return due
  return ETHIOPIAN_MONTH_DAYS
}

const totalDays = computed(() => resolveTotalDays())

function buildEmployeesForDepartment(row) {
  const users = getSingleDepartmentUsers(row.id, row.departmentName)
  return mapUsersToAttendanceEmployees(users)
}

function buildEmployeesForMultiDepartment() {
  return mapUsersToAttendanceEmployees(multiDepartmentUsers.value)
}

function mapUsersToAttendanceEmployees(users) {
  return users
    .map((u) => ({
      userId: Number(u.id),
      companyId: u.companyId != null ? Number(u.companyId) : null,
      branchId: u.branchId != null ? Number(u.branchId) : null,
      empId: `U${String(u.id ?? '').trim()}`,
      empName: String(u.fullName || u.username || '—'),
      role: String(u.role || '—'),
      // Default Due Date is always 30 (Ethiopian month) until a saved value exists.
      totalDays: ETHIOPIAN_MONTH_DAYS,
      overtimeSelections: {},
      absent: null,
      leaveDays: null
    }))
    .sort((a, b) => a.empName.localeCompare(b.empName))
}


function overtimeJsonToSelections(overtime) {
  const out = {}
  if (!Array.isArray(overtime)) return out
  for (const row of overtime) {
    const id = String(row?.ot_id ?? row?.otId ?? '').trim()
    const hrs = Number(row?.hrs ?? row?.hours ?? 0)
    if (id && Number.isFinite(hrs) && hrs > 0) out[id] = hrs
  }
  return out
}

function buildAttendancePeriodQuery() {
  const params = new URLSearchParams()
  if (attendanceDetailModal.isMultiDepartment) {
    const scopeParams = buildMultiDepartmentScopeQuery()
    scopeParams.forEach((value, key) => {
      params.set(key, value)
    })
    return params
  }

  const depId = normalizeInt(attendanceDetailModal.departmentId)
  if (depId) params.set('departmentId', String(depId))
  if (filters.selectedMonth) {
    params.set('month', String(filters.selectedMonth))
  } else if (filters.fromDate && filters.toDate) {
    params.set('fromDate', filters.fromDate)
    params.set('toDate', filters.toDate)
  }
  return params
}

function attendanceRecordsUrl() {
  const params = buildAttendancePeriodQuery()
  if (attendanceDetailModal.isMultiDepartment) {
    return `${monthlyConfigurationApiPath.value}/attendance-multi-department-records?${params.toString()}`
  }
  return `${monthlyConfigurationApiPath.value}/attendance?${params.toString()}`
}

function normalizeInt(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null
  const n = parseInt(String(value), 10)
  return Number.isNaN(n) ? null : n
}

function hasValidAttendancePeriod() {
  if (filters.selectedMonth) return true
  return Boolean(filters.fromDate && filters.toDate)
}

async function loadSavedAttendanceForModal() {
  savedAttendanceRecords.value = []
  if (!hasValidAttendancePeriod()) return
  const params = buildAttendancePeriodQuery()
  if (!params.get('month') && !(params.get('fromDate') && params.get('toDate'))) return

  suppressAttendanceDraftPersistence = true
  try {
    const response = await apiFetch(attendanceRecordsUrl(), { method: 'GET' })
    if (!response.ok) return
    const rows = await response.json().catch(() => [])
    if (!Array.isArray(rows)) return

    savedAttendanceRecords.value = rows

    if (!rows.length) return

    const byUserId = new Map(rows.map((r) => [String(r.userId), r]))
    for (const emp of attendanceDetailModal.employees) {
      const saved = byUserId.get(String(emp.userId))
      if (!saved) continue
      emp.overtimeSelections = overtimeJsonToSelections(saved.overtime)
      emp.absent = saved.absent != null ? Number(saved.absent) : null
      emp.leaveDays = saved.leaveDays != null ? Number(saved.leaveDays) : null
      if (saved.dueDate != null) emp.totalDays = resolveAttendanceDueDate(saved.dueDate)
    }
  } catch (err) {
    console.error('Error loading saved attendance:', err)
    savedAttendanceRecords.value = []
  } finally {
    await nextTick()
    suppressAttendanceDraftPersistence = false
  }
}

async function onDetail(row) {
  attendanceDetailModal.isMultiDepartment = false
  attendanceDetailModal.departmentId = row.id
  attendanceDetailModal.departmentName = row.departmentName
  attendanceDetailModal.employees = buildEmployeesForDepartment(row)
  await loadSavedAttendanceForModal()
  const draft = loadAttendanceDraftForCurrentContext()
  if (draft) {
    // Stale draft that matches already-saved attendance is not "unsaved changes".
    if (savedAttendanceRecords.value.length > 0 && draftPayloadMatchesSaved(draft)) {
      clearAttendanceDraftForCurrentContext()
    } else {
      applyDraftToEmployees(draft)
    }
  }
  attendanceDetailModal.show = true
}

async function onMultiDepartmentDetail() {
  if (isLoadingMultiDepartmentUsers.value || multiDepartmentUsersCount.value === 0) return
  attendanceDetailModal.isMultiDepartment = true
  attendanceDetailModal.departmentId = MULTI_DEPARTMENT_MODAL_ID
  attendanceDetailModal.departmentName = 'Multiple Departments'
  attendanceDetailModal.employees = buildEmployeesForMultiDepartment()
  await loadSavedAttendanceForModal()
  const draft = loadAttendanceDraftForCurrentContext()
  if (draft) {
    if (savedAttendanceRecords.value.length > 0 && draftPayloadMatchesSaved(draft)) {
      clearAttendanceDraftForCurrentContext()
    } else {
      applyDraftToEmployees(draft)
    }
  }
  attendanceDetailModal.show = true
}

function closeAttendanceDetailModal() {
  closeOvertimePanel()
  attendanceDetailModal.show = false
  attendanceDetailModal.departmentId = null
  attendanceDetailModal.departmentName = ''
  attendanceDetailModal.isMultiDepartment = false
  attendanceDetailModal.employees = []
  savedAttendanceRecords.value = []
}

function overtimeButtonLabel(emp) {
  const selections = emp?.overtimeSelections && typeof emp.overtimeSelections === 'object'
    ? emp.overtimeSelections
    : {}
  const count = Object.values(selections).filter((v) => Number(v) > 0).length
  if (!count) return 'Select OT'
  return `${count} selected`
}

function fmtOvertimeTotalHours(emp) {
  const selections = emp?.overtimeSelections && typeof emp.overtimeSelections === 'object'
    ? emp.overtimeSelections
    : {}
  const total = Object.values(selections).reduce((sum, v) => {
    const n = Number(v)
    return Number.isFinite(n) && n > 0 ? sum + n : sum
  }, 0)
  return total > 0 ? total.toFixed(2) : '0.00'
}

function getActivePanelEmployee() {
  const empId = String(overtimePanel.empId || '')
  if (!empId) return null
  return attendanceDetailModal.employees.find((emp) => String(emp.empId || '') === empId) || null
}

function normalizeHoursValue(raw) {
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.round(n * 100) / 100
}

function overtimeHoursForCurrent(otId) {
  const emp = getActivePanelEmployee()
  if (!emp) return ''
  const val = Number(emp?.overtimeSelections?.[String(otId)] || 0)
  return val > 0 ? String(val) : ''
}

function setOvertimeHoursForCurrent(otId, raw) {
  const emp = getActivePanelEmployee()
  if (!emp) return
  if (!emp.overtimeSelections || typeof emp.overtimeSelections !== 'object') {
    emp.overtimeSelections = {}
  }
  const key = String(otId)
  const val = normalizeHoursValue(raw)
  if (val <= 0 || raw === '' || raw == null) {
    delete emp.overtimeSelections[key]
    schedulePersistAttendanceDraft()
    return
  }
  emp.overtimeSelections[key] = val
  schedulePersistAttendanceDraft()
}

const OT_PANEL_PAD = 8
const OT_PANEL_GAP = 6
const OT_PANEL_WIDTH = 352
const OT_PANEL_MAX_H = 400
const OT_PANEL_MIN_H = 160

const ATTENDANCE_DRAFT_PREFIX = 'finance_monthly_configuration_attendance_draft_v1'
let attendanceDraftTimer = null
let suppressAttendanceDraftPersistence = false
const attendanceDraftRevision = ref(0)

function touchAttendanceDraftRevision() {
  attendanceDraftRevision.value += 1
}

function safeJsonParse(raw) {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function periodStorageKeyPart() {
  if (filters.selectedMonth) return `m:${String(filters.selectedMonth)}`
  if (filters.fromDate && filters.toDate) return `r:${filters.fromDate}_${filters.toDate}`
  return 'none'
}

function attendanceDraftKey() {
  return attendanceDraftKeyForDepartment(attendanceDetailModal.departmentId)
}

function buildDraftPayload() {
  return {
    departmentId: attendanceDetailModal.departmentId,
    month: filters.selectedMonth || null,
    fromDate: filters.fromDate || null,
    toDate: filters.toDate || null,
    dueDate: resolveTotalDays(),
    employees: buildSaveEmployeesPayload()
  }
}

function hasAnyFilledAttendanceInput() {
  return attendanceDetailModal.employees.some((emp) => {
    const hasAbsent = emp?.absent !== null && emp?.absent !== undefined && emp?.absent !== ''
    const hasLeaveDays = emp?.leaveDays !== null && emp?.leaveDays !== undefined && emp?.leaveDays !== ''
    const overtime = emp?.overtimeSelections && typeof emp.overtimeSelections === 'object'
      ? emp.overtimeSelections
      : {}
    const hasOvertime = Object.values(overtime).some((v) => {
      const n = Number(v)
      return Number.isFinite(n) && n > 0
    })
    return hasAbsent || hasLeaveDays || hasOvertime
  })
}

function normalizeOtMap(overtime) {
  if (overtime == null) return {}
  if (typeof overtime === 'string') {
    return normalizeOtMap(safeJsonParse(overtime))
  }
  if (Array.isArray(overtime)) return overtimeJsonToSelections(overtime)
  if (typeof overtime === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(overtime)) {
      const n = Number(v)
      if (Number.isFinite(n) && n > 0) out[String(k)] = n
    }
    return out
  }
  return {}
}

function otMapsEqual(a, b) {
  const left = a && typeof a === 'object' ? a : {}
  const right = b && typeof b === 'object' ? b : {}
  const keys = new Set([...Object.keys(left), ...Object.keys(right)])
  for (const key of keys) {
    if (Number(left[key] || 0) !== Number(right[key] || 0)) return false
  }
  return true
}

function employeeSnapshotFromForm(emp) {
  const ot = normalizeOtMap(emp?.overtimeSelections)
  return {
    absent: emp?.absent != null && emp?.absent !== '' ? Number(emp.absent) : 0,
    leaveDays: emp?.leaveDays != null && emp?.leaveDays !== '' ? Number(emp.leaveDays) : 0,
    ot
  }
}

function employeeSnapshotFromSaved(saved) {
  return {
    absent: saved?.absent != null ? Number(saved.absent) : 0,
    leaveDays: saved?.leaveDays != null ? Number(saved.leaveDays) : 0,
    ot: normalizeOtMap(saved?.overtime)
  }
}

function employeeSnapshotFromDraftEmp(emp) {
  const leaveDaysValue = emp?.leaveDays ?? emp?.liveDays
  return {
    absent: emp?.absent != null ? Number(emp.absent) : 0,
    leaveDays: leaveDaysValue != null ? Number(leaveDaysValue) : 0,
    ot: normalizeOtMap(emp?.overtime)
  }
}

function snapshotHasData(snap) {
  return Boolean(
    (Number(snap?.absent) || 0) !== 0 ||
    (Number(snap?.leaveDays) || 0) !== 0 ||
    Object.keys(snap?.ot || {}).length > 0
  )
}

function snapshotsEqual(a, b) {
  return (
    Number(a?.absent || 0) === Number(b?.absent || 0) &&
    Number(a?.leaveDays || 0) === Number(b?.leaveDays || 0) &&
    otMapsEqual(a?.ot, b?.ot)
  )
}

/** True when modal form matches already-saved attendance (no unsaved edits). */
function isCurrentFormSameAsSaved() {
  if (!Array.isArray(savedAttendanceRecords.value) || !savedAttendanceRecords.value.length) {
    return false
  }
  const byUserId = new Map(savedAttendanceRecords.value.map((r) => [String(r.userId), r]))
  for (const emp of attendanceDetailModal.employees) {
    const saved = byUserId.get(String(emp.userId))
    const formSnap = employeeSnapshotFromForm(emp)
    if (!saved) {
      if (snapshotHasData(formSnap)) return false
      continue
    }
    if (!snapshotsEqual(formSnap, employeeSnapshotFromSaved(saved))) return false
  }
  return true
}

/** True when a localStorage draft payload matches already-saved attendance. */
function draftPayloadMatchesSaved(draft) {
  if (!draft || !Array.isArray(draft.employees)) return false
  if (!Array.isArray(savedAttendanceRecords.value) || !savedAttendanceRecords.value.length) {
    return false
  }
  const byUserId = new Map(savedAttendanceRecords.value.map((r) => [String(r.userId), r]))
  for (const emp of draft.employees) {
    const uid = String(emp?.userId ?? emp?.user_id ?? '')
    if (!uid) continue
    const saved = byUserId.get(uid)
    const draftSnap = employeeSnapshotFromDraftEmp(emp)
    if (!saved) {
      if (snapshotHasData(draftSnap)) return false
      continue
    }
    if (!snapshotsEqual(draftSnap, employeeSnapshotFromSaved(saved))) return false
  }
  return true
}

function cancelScheduledAttendanceDraft() {
  if (attendanceDraftTimer) {
    window.clearTimeout(attendanceDraftTimer)
    attendanceDraftTimer = null
  }
}

function persistAttendanceDraftNow() {
  if (suppressAttendanceDraftPersistence) return
  if (!attendanceDetailModal.show) return
  if (!attendanceDetailModal.departmentId) return
  if (!hasValidAttendancePeriod()) return

  const key = attendanceDraftKey()
  // Draft only when there are filled inputs that differ from saved records.
  if (!hasAnyFilledAttendanceInput() || isCurrentFormSameAsSaved()) {
    try {
      localStorage.removeItem(key)
      touchAttendanceDraftRevision()
    } catch {
      // ignore
    }
    return
  }

  const payload = buildDraftPayload()
  try {
    localStorage.setItem(key, JSON.stringify(payload))
    touchAttendanceDraftRevision()
  } catch (err) {
    console.warn('Unable to persist attendance draft:', err)
  }
}

function schedulePersistAttendanceDraft() {
  if (suppressAttendanceDraftPersistence) return
  cancelScheduledAttendanceDraft()
  attendanceDraftTimer = window.setTimeout(() => {
    attendanceDraftTimer = null
    persistAttendanceDraftNow()
  }, 250)
}

function loadAttendanceDraftForCurrentContext() {
  const key = attendanceDraftKey()
  let raw = null
  try {
    raw = localStorage.getItem(key)
  } catch {
    raw = null
  }
  if (!raw) return null
  const parsed = safeJsonParse(raw)
  if (!parsed || typeof parsed !== 'object') return null

  if (String(parsed.departmentId || '') !== String(attendanceDetailModal.departmentId || '')) return null
  const monthMatch = String(parsed.month || '') === String(filters.selectedMonth || '')
  const rangeMatch =
    String(parsed.fromDate || '') === String(filters.fromDate || '') &&
    String(parsed.toDate || '') === String(filters.toDate || '')
  if (!(monthMatch || rangeMatch)) return null

  return parsed
}

function applyDraftToEmployees(draft) {
  if (!draft || !Array.isArray(draft.employees)) return

  const byUserId = new Map(
    draft.employees
      .filter((e) => e && (e.userId != null || e.user_id != null))
      .map((e) => [String(e.userId ?? e.user_id), e])
  )

  for (const emp of attendanceDetailModal.employees) {
    const saved = byUserId.get(String(emp.userId))
    if (!saved) continue
    emp.overtimeSelections = overtimeJsonToSelections(saved.overtime)
    // HR + attendance device: Absent / Leave Days stay locked — do not restore from draft.
    if (!lockAbsentLeaveDays.value) {
      emp.absent = saved.absent != null ? Number(saved.absent) : emp.absent
      const leaveDaysValue = saved.leaveDays ?? saved.liveDays
      emp.leaveDays = leaveDaysValue != null ? Number(leaveDaysValue) : emp.leaveDays
    }
    if (saved.dueDate != null) emp.totalDays = resolveAttendanceDueDate(saved.dueDate)
  }
}

function clearAttendanceDraftForCurrentContext() {
  const key = attendanceDraftKey()
  try {
    localStorage.removeItem(key)
    touchAttendanceDraftRevision()
  } catch {
    // ignore
  }
}

function openOvertimePanel(evt, emp) {
  const rect = evt.currentTarget.getBoundingClientRect()
  overtimePanel.empId = String(emp?.empId || '')
  overtimePanel.open = true

  const spaceBelow = window.innerHeight - rect.bottom - OT_PANEL_PAD
  const spaceAbove = rect.top - OT_PANEL_PAD
  const openAbove = spaceBelow < OT_PANEL_MIN_H && spaceAbove > spaceBelow
  const available = Math.max(
    OT_PANEL_MIN_H,
    (openAbove ? spaceAbove : spaceBelow) - OT_PANEL_GAP
  )

  overtimePanel.maxH = Math.min(OT_PANEL_MAX_H, available)
  overtimePanel.left = Math.min(
    Math.max(OT_PANEL_PAD, rect.left),
    window.innerWidth - OT_PANEL_WIDTH - OT_PANEL_PAD
  )
  overtimePanel.top = openAbove
    ? Math.max(OT_PANEL_PAD, rect.top - OT_PANEL_GAP - overtimePanel.maxH)
    : rect.bottom + OT_PANEL_GAP

  nextTick(() => {
    bindOutsideOvertimeClose()
  })
}

function closeOvertimePanel() {
  overtimePanel.open = false
  overtimePanel.empId = ''
  removeOutsideOvertimeClose()
}

let overtimeOutsideHandler = null
function bindOutsideOvertimeClose() {
  removeOutsideOvertimeClose()
  overtimeOutsideHandler = (e) => {
    const panel = overtimePanelEl.value
    if (panel && panel.contains(e.target)) return
    closeOvertimePanel()
  }
  document.addEventListener('pointerdown', overtimeOutsideHandler)
}

function removeOutsideOvertimeClose() {
  if (!overtimeOutsideHandler) return
  document.removeEventListener('pointerdown', overtimeOutsideHandler)
  overtimeOutsideHandler = null
}

function onEmployeeDetail(emp) {
  console.log('Employee attendance detail:', emp)
}

function onEmployeeEdit(emp) {
  console.log('Employee attendance edit:', emp)
}

async function onApprovalAction(key) {
  const action = otherApprovalActions.value.find((a) => a.key === key)
  if (!action?.canClick || isUpdatingApproval.value) return

  if (!hasValidAttendancePeriod()) {
    showToast('Select a month or date range before approval.', 'error')
    return
  }

  if (!savedAttendanceRecords.value.length) {
    showToast('No saved attendance records available for approval.', 'error')
    return
  }

  const userIds = savedAttendanceRecords.value
    .map((row) => row.userId)
    .filter((id) => Number.isFinite(Number(id)) && Number(id) > 0)

  if (!userIds.length) {
    showToast('No employees available for approval.', 'error')
    return
  }

  const confirmLabel = action.baseLabel || key
  if (!window.confirm(`Are you sure you want to record "${confirmLabel}"?`)) return

  isUpdatingApproval.value = true
  try {
    const approvalUrl = attendanceDetailModal.isMultiDepartment
      ? `${monthlyConfigurationApiPath.value}/attendance-multi-department/approval`
      : `${monthlyConfigurationApiPath.value}/attendance/approval`

    const approvalBody = {
      month: filters.selectedMonth || null,
      fromDate: filters.fromDate || null,
      toDate: filters.toDate || null,
      step: key,
      userIds
    }

    if (attendanceDetailModal.isMultiDepartment) {
      const scopeParams = buildMultiDepartmentScopeQuery()
      approvalBody.selection = scopeParams.get('selection')
      const branchId = scopeParams.get('branchId')
      if (branchId) approvalBody.branchId = branchId
    } else {
      approvalBody.departmentId = attendanceDetailModal.departmentId
    }

    const response = await apiFetch(approvalUrl, {
      method: 'PATCH',
      body: JSON.stringify(approvalBody)
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      showToast(data.message || 'Failed to update approval.', 'error')
      return
    }
    const label = action.baseLabel || key
    showToast(`${label} recorded.`, 'success')
    await loadSavedAttendanceForModal()
    clearAttendanceDraftForCurrentContext()
    await loadDepartments(filters.selectedBranchId)
  } catch (err) {
    console.error('Error updating attendance approval:', err)
    showToast('Failed to update approval.', 'error')
  } finally {
    isUpdatingApproval.value = false
  }
}

function overtimePercentForOtId(otId) {
  const id = String(otId)
  const opt = overtimeOptions.value.find((row) => String(row.id) === id)
  if (!opt || opt.otRate == null || opt.otRate === '') return null
  const percent = Number(opt.otRate)
  return Number.isFinite(percent) ? percent : null
}

function buildOvertimePayload(emp) {
  const selections = emp?.overtimeSelections && typeof emp.overtimeSelections === 'object'
    ? emp.overtimeSelections
    : {}
  return Object.entries(selections)
    .map(([otId, hrs]) => {
      const row = {
        ot_id: Number(otId),
        hrs: Number(hrs)
      }
      const percent = overtimePercentForOtId(otId)
      if (percent != null) row.percent = percent
      return row
    })
    .filter((row) => Number.isFinite(row.ot_id) && row.ot_id > 0 && Number.isFinite(row.hrs) && row.hrs > 0)
}

function buildSaveEmployeesPayload() {
  return attendanceDetailModal.employees.map((emp) => {
    const overtime = buildOvertimePayload(emp)
    const totalHrs = overtime.reduce((sum, row) => sum + row.hrs, 0)
    return {
      userId: emp.userId,
      companyId: emp.companyId,
      branchId: emp.branchId,
      dueDate: resolveAttendanceDueDate(emp.totalDays),
      overtime,
      totalHrs,
      absent: emp.absent != null && emp.absent !== '' ? Number(emp.absent) : 0,
      leaveDays: emp.leaveDays != null && emp.leaveDays !== '' ? Number(emp.leaveDays) : 0
    }
  })
}

async function onSave() {
  if (!canPrepareAttendance.value) return
  if (isSaving.value) return
  if (!hasValidAttendancePeriod()) {
    showToast('Select a month or both From Date and To Date.', 'error')
    return
  }
  if (!attendanceDetailModal.employees.length) {
    showToast('No employees to save.', 'error')
    return
  }

  isSaving.value = true
  // Prevent draft timer from re-writing localStorage after a successful save.
  suppressAttendanceDraftPersistence = true
  cancelScheduledAttendanceDraft()
  try {
    const response = await apiFetch(`${monthlyConfigurationApiPath.value}/attendance`, {
      method: 'POST',
      body: JSON.stringify({
        departmentId: attendanceDetailModal.isMultiDepartment ? null : attendanceDetailModal.departmentId,
        month: filters.selectedMonth || null,
        fromDate: filters.fromDate || null,
        toDate: filters.toDate || null,
        dueDate: resolveTotalDays(),
        employees: buildSaveEmployeesPayload()
      })
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      showToast(data.message || 'Failed to save attendance.', 'error')
      return
    }
    showToast(data.message || 'Attendance saved successfully.', 'success')
    clearAttendanceDraftForCurrentContext()
    await loadDepartments(filters.selectedBranchId)
    await loadMultiDepartmentUsers()
    closeAttendanceDetailModal()
  } catch (err) {
    console.error('Error saving attendance:', err)
    showToast('Failed to save attendance.', 'error')
  } finally {
    suppressAttendanceDraftPersistence = false
    isSaving.value = false
  }
}

watch(
  () => attendanceDetailModal.employees,
  () => {
    if (!attendanceDetailModal.show) return
    schedulePersistAttendanceDraft()
  },
  { deep: true }
)

function overtimeIsActive(row) {
  if (!row || typeof row !== 'object') return false
  const active = row.active
  if (active === undefined || active === null) return false
  return active === true || active === 1 || active === '1'
}

async function loadOvertimeOptions() {
  try {
    const response = await apiFetch(`${props.apiBase}/overtime`, { method: 'GET' })
    if (!response.ok) {
      overtimeOptions.value = []
      return
    }
    const data = await response.json().catch(() => [])
    const rows = Array.isArray(data) ? data : []
    overtimeOptions.value = rows
      .filter(overtimeIsActive)
      .map((row) => ({
        id: String(row.id),
        otName: String(row.otName || '').trim() || 'Overtime',
        otRate: row.otRate != null && row.otRate !== '' ? Number(row.otRate) : null
      }))
      .sort((a, b) => a.otName.localeCompare(b.otName))
  } catch (err) {
    console.error('Error loading overtime definitions:', err)
    overtimeOptions.value = []
  }
}

watch(
  () => filters.selectedBranchId,
  (branchId) => {
    const fixed = String(tokenBranchId.value || '').trim()
    if (fixed && String(branchId || '').trim() !== fixed) {
      filters.selectedBranchId = fixed
      return
    }
    filters.departmentId = ''
    loadDepartments(branchId)
    loadMultiDepartmentUsers()
  },
  { immediate: true }
)

watch(
  () => [filters.selectedMonth, filters.fromDate, filters.toDate],
  () => {
    const branchId = String(filters.selectedBranchId || '').trim()
    if (!branchId) return
    if (!hasValidAttendancePeriod()) {
      multiDepartmentUsers.value = []
      return
    }
    loadDepartments(branchId)
    loadMultiDepartmentUsers()
  }
)

const emptyStateMessage = computed(() => {
  if (!String(filters.selectedBranchId || '').trim()) return 'Select Branch to load departments.'
  return 'No departments found.'
})

onMounted(() => {
  loadBranches().then(() => {
    if (tokenBranchId.value) {
      filters.selectedBranchId = tokenBranchId.value
    }
  })
  loadOvertimeOptions()
})

onBeforeUnmount(() => {
  removeOutsideOvertimeClose()
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  if (attendanceDraftTimer) {
    window.clearTimeout(attendanceDraftTimer)
    attendanceDraftTimer = null
  }
})
</script>

<style scoped>
.attendance-spinner {
  animation: attendanceSpin 0.9s linear infinite;
}

@keyframes attendanceSpin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.attendance-table,
.attendance-detail-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #1e293b;
}

.attendance-table th,
.attendance-table td,
.attendance-detail-table th,
.attendance-detail-table td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  font-size: 0.7rem;
}

.attendance-table th,
.attendance-detail-table th {
  background: #f1f5f9;
  font-weight: 600;
}

.attendance-table tbody tr:hover,
.attendance-detail-table tbody tr:hover {
  background: #f1f5f9;
}

.attendance-table .empty-cell {
  text-align: center;
  color: #94a3b8;
  padding: 32px 15px;
  font-style: italic;
}

.attendance-table td.status-drafted {
  color: #d97706;
  font-weight: 600;
}

.attendance-table td.status-approved {
  color: #16a34a;
  font-weight: 600;
}

.attendance-table td.status-new {
  color: #dc2626;
  font-weight: 600;
}

.attendance-detail-table th.num,
.attendance-detail-table td.num {
  width: 5.5rem;
  min-width: 5.5rem;
  text-align: center;
  white-space: nowrap;
}

.attendance-detail-table input.income-table-field {
  box-sizing: border-box;
  width: 100%;
  padding: 2px 4px;
  border: 1px solid transparent;
  border-radius: 3px;
  outline: none;
  background-color: #fef2f2;
  font-size: 0.72rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
  transition: all 0.15s;
}

.attendance-detail-table input.income-table-field:focus {
  border-color: #0d4169;
  background-color: #fff;
}

.ot-action-btn {
  width: 100%;
  padding: 2px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #0d4169;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
}

.ot-action-btn:hover {
  border-color: #0d4169;
}

.ot-hours-input {
  width: 3.9rem;
  padding: 2px 4px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  outline: none;
  text-align: center;
  font-size: 0.72rem;
  font-weight: 600;
  color: #0d4169;
}

.ot-hours-input:focus {
  border-color: #0d4169;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.multi-dept-trigger {
  min-width: 9rem;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.625rem;
  border: 1px solid #fca5a5;
  border-radius: 0.375rem;
  background: #fff7f7;
  color: #dc2626;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s;
}

.multi-dept-trigger:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #f87171;
}

.multi-dept-trigger:focus-visible {
  outline: none;
  border-color: #dc2626;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.1);
}

.multi-dept-trigger:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
